import {Injectable} from '@angular/core';
import {FormArray, FormGroup} from '@angular/forms';
import {BehaviorSubject, Subject} from 'rxjs';
import {FlatField} from '../model/flat-field';
import {IndexTrackerService} from './index-tracker.service';
import {DuplicableElementPathEnum} from "../enum/duplicable-element-id-path.enum";

/**
 * Service de gestion des champs plats (flatFields) dans les formulaires eForm.
 *
 * Ce service gère le scan, la mise à jour et le stockage des champs sous forme
 * de map indexée par inputId pour un accès rapide.
 *
 * @version 1.0
 */
@Injectable({
  providedIn: 'root'
})
export class EformFlatFieldsService {
  /** Map centralisée contenant tous les champs indexés par inputId pour un accès rapide */
  private flatFields = new BehaviorSubject<Map<string, FlatField[]>>(new Map());

  /** Flag indiquant si les flatFields ont été initialisés et sont prêts à être utilisés */
  eformFlatFieldsReady: boolean = false;

  /** Subject pour notifier les composants qu'une mise à jour de flatFields a eu lieu après une suppression */
  public flatFieldsUpdatedAfterDeletion$ = new Subject<{ type: string, index: number }>();

  /** Mutex pour prévenir les rescans concurrents */
  private isRescanInProgress = false;

  /** Map pour stocker l'index réel affiché par type d'élément duplicable */
  private displayedRealIndexByType = new Map<string, number>();

  /** Fonction de callback pour récupérer displayedElementIndex comme fallback */
  private getDisplayedElementIndexFallback: (type: string) => number = () => 0;

  /**
   * Map des résultats d'évaluations de l'interdiction des identifiants de groupes répétables (CON/TEN/TPA),
   * indexé par `${type}-${index}. Alimenté par FormInputComponent (qui évalue avec le bon
   * repeatablePath de l'élément affiché) et consommé par l'extracteur lors de la régénération
   * des identifiants. Vidé au chargement d'un avis (resetFlatFields).
   */
  private repeatableGroupForbiddenEvaluations = new Map<string, boolean>();

  constructor(
    private indexTrackerService: IndexTrackerService
  ) {
    this.flatFields.next(new Map());
  }

  /**
   * Configure la fonction de fallback pour récupérer displayedElementIndex
   * Cela évite les dépendances circulaires avec EformDuplicableEntityService
   */
  setDisplayedElementIndexFallback(fn: (type: string) => number): void {
    this.getDisplayedElementIndexFallback = fn;
  }

  /**
   * Définit l'index réel affiché pour un type d'élément duplicable.
   * Cet index sera utilisé lors du scan pour stocker correctement les valeurs dans flatFields.
   * @param type Le type d'élément duplicable (ORG, CON, TEN, etc.)
   * @param realIndex L'index réel de l'élément dans la collection complète
   */
  setDisplayedRealIndex(type: string, realIndex: number): void {
    this.displayedRealIndexByType.set(type, realIndex);
  }

  /**
   * Récupère l'index réel affiché pour un type d'élément duplicable.
   * @param type Le type d'élément duplicable
   * @returns L'index réel ou undefined si non défini
   */
  getDisplayedRealIndex(type: string): number | undefined {
    return this.displayedRealIndexByType.get(type);
  }

  /**
   * Efface tous les index réels affichés.
   */
  clearDisplayedRealIndexes(): void {
    this.displayedRealIndexByType.clear();
  }

  /**
   * Enregistre l'évaluation de l'interdiction d'un identifiant de groupe répétable pour un élément donné.
   */
  setRepeatableGroupForbiddenEvaluation(groupType: string, groupIndex: number, forbidden: boolean): void {
    this.repeatableGroupForbiddenEvaluations.set(this.buildForbiddenEvaluationKey(groupType, groupIndex), forbidden);
  }

  /**
   * Retourne l'évaluation de l'interdiction enregistrée pour un élément, ou undefined si jamais évalué.
   */
  getRepeatableGroupForbiddenEvaluation(groupType: string, groupIndex: number): boolean | undefined {
    return this.repeatableGroupForbiddenEvaluations.get(this.buildForbiddenEvaluationKey(groupType, groupIndex));
  }

  private buildForbiddenEvaluationKey(groupType: string, groupIndex: number): string {
    return `${groupType}-${groupIndex}`;
  }

  /**
   * Retourne l'observable des flatFields
   */
  get flatFields$() {
    return this.flatFields.asObservable();
  }

  /**
   * Retourne la valeur actuelle des flatFields
   */
  get flatFieldsValue(): Map<string, FlatField[]> {
    return this.flatFields.value;
  }

  /**
   * Met à jour la valeur des flatFields
   */
  setFlatFields(value: Map<string, FlatField[]>): void {
    this.flatFields.next(value);
  }

  /**
   * Réinitialise la map flatFields.
   */
  resetFlatFields(): void {
    this.flatFields.next(new Map());
    this.eformFlatFieldsReady = false;
    this.repeatableGroupForbiddenEvaluations.clear();
  }

  /**
   * Reconstruit la map flatFields à partir du formulaire courant.
   * @param formGroup Le FormGroup à analyser
   * @param cleanupCallback Callback optionnel pour le nettoyage des champs interdits
   */
  rescanFlatFields(formGroup: FormGroup, cleanupCallback?: (formGroup: FormGroup) => void): void {
    // Prévenir les cycles en bloquant les nouveaux rescans pendant l'exécution
    if (this.isRescanInProgress) {
      return;
    }

    this.isRescanInProgress = true;

    try {
      const currentFields = this.flatFields.value;
      const newFlatFields = new Map<string, FlatField[]>();

      // Scanner les champs du formulaire
      this.scanFormGroupToFlatFields(formGroup, '', newFlatFields);

      // 🔧 FIX 2026-05-21: Capturer un snapshot de newFlatFields AVANT la boucle de préservation.
      // Ce snapshot représente uniquement ce que le scan du FormGroup a trouvé.
      // Sans ce snapshot, newFlatFields est modifié au fur et à mesure dans la boucle :
      // quand ORG-0003 (TPA_0, idx=0) est préservé, l'itération suivante pour ORG-0004
      // (même TPA_0, idx=0) trouvait déjà ORG-0003 → scanFoundSameTypeAndIndex = true → ORG-0004 perdu.
      const scannedFromFormGroup = new Map<string, FlatField[]>();
      newFlatFields.forEach((fields, inputId) => {
        scannedFromFormGroup.set(inputId, [...fields]);
      });

      // Conserver les champs des autres éléments duplicables qui ne sont pas dans le formulaire actuel
      currentFields.forEach((fields, inputId) => {
        fields.forEach(field => {
          // Pour les champs duplicables, conserver ceux qui ont un duplicableIndex différent de celui scanné
          if (field.isDuplicableField && field.duplicableType && field.duplicableIndex !== undefined) {
            // 🔧 FIX 2026-04-21: Ne PAS préserver les entrées de l'élément duplicable actuellement affiché.
            // Le scan du formGroup est autoritatif pour l'élément affiché.
            // Détection : si le scan a déjà trouvé des entrées pour cet inputId avec le même
            // duplicableType+duplicableIndex, c'est que l'élément est chargé dans le formulaire.
            // Les entrées "orphelines" (path différent, ex: tenderer supprimé) ne doivent pas être préservées.
            // 🔧 FIX 2026-05-21: Utiliser scannedFromFormGroup (snapshot avant boucle) pour éviter
            // que les entrées préservées dans les itérations précédentes n'interfèrent.
            const scannedFields = scannedFromFormGroup.get(inputId) || [];

            const scanFoundSameTypeAndIndex = scannedFields.some(nf =>
              nf.isDuplicableField &&
              nf.duplicableType === field.duplicableType &&
              nf.duplicableIndex === field.duplicableIndex
            );

            if (scanFoundSameTypeAndIndex) {
              // L'élément est chargé dans le formulaire. Cette ancienne entrée a un path différent
              // (ex: d'un sous-groupe supprimé comme GR-Tenderer). Ne pas la préserver.
              return;
            }

            // 🔧 FIX 2026-03-17: Vérifier aussi le path pour distinguer les champs enfants
            // qui partagent le même duplicableType/Index (ex: tenderer.0 et tenderer.1 du même TPA)
            // Sans le path, le 2ème tenderer est considéré comme doublon et perdu
            // Note: on utilise newFlatFields (état courant, incluant les entrées déjà préservées)
            // pour éviter de préserver deux fois la même entrée.
            const currentNewFields = newFlatFields.get(inputId) || [];
            const alreadyExists = currentNewFields.some(nf =>
              nf.isDuplicableField &&
              nf.duplicableType === field.duplicableType &&
              nf.duplicableIndex === field.duplicableIndex &&
              nf.path === field.path
            );

            if (!alreadyExists) {
              // Marquer comme non affiché et conserver
              if (!newFlatFields.has(inputId)) {
                newFlatFields.set(inputId, []);
              }
              newFlatFields.get(inputId)!.push({
                ...field,
                isDisplayed: false
              });
            }
          } else if (!field.isDisplayed) {
            // Pour les champs non duplicables, garder la logique originale
            const existingPaths = newFlatFields.get(inputId)?.map(f => f.path) || [];
            if (!existingPaths.includes(field.path)) {
              if (!newFlatFields.has(inputId)) {
                newFlatFields.set(inputId, []);
              }
              newFlatFields.get(inputId)!.push(field);
            }
          }
        });
      });

      this.flatFields.next(newFlatFields);


      // Émettre un événement pour forcer la réévaluation de shouldDisplayField()
      this.flatFieldsUpdatedAfterDeletion$.next({type: 'RESCAN', index: -1});

      // Callback pour le nettoyage des champs interdits
      if (cleanupCallback) {
        cleanupCallback(formGroup);
      }
    } finally {
      this.isRescanInProgress = false;
    }
  }

  /**
   * Force un rescan de flatFields après la suppression d'un élément duplicable.
   *
   * @param formGroup Le FormGroup à rescanner
   * @param type Le type d'élément duplicable supprimé (LOT, ORG, etc.)
   * @param index L'index de l'élément supprimé
   */
  forceRescanAfterDeletion(formGroup: FormGroup, type: string, index: number): void {
    this.rescanFlatFields(formGroup);
    this.flatFieldsUpdatedAfterDeletion$.next({type, index});
  }

  /**
   * Parcourt récursivement un FormGroup et remplit la map flatFields.
   */
  scanFormGroupToFlatFields(group: FormGroup, parentPath: string, flatFieldsMap: Map<string, FlatField[]>): void {
    Object.keys(group.controls).forEach(key => {
      const control = group.get(key);
      const currentPath = parentPath ? `${parentPath}.${key}` : key;


      if (control instanceof FormArray) {
        this.scanFormArrayToFlatFields(control, currentPath, flatFieldsMap);
      } else if (control instanceof FormGroup) {
        const rawValue = control.getRawValue();
        if (this.hasInputId(rawValue)) {
          const fieldData = this.createFlatFieldFromRawValue(control, currentPath, rawValue);

          this.addFlatFieldToMap(flatFieldsMap, rawValue.inputId, fieldData);

          // Enregistrer aussi les alternateInputIds pour les champs qui partagent le même jsonPath
          if (rawValue.alternateInputIds && Array.isArray(rawValue.alternateInputIds)) {
            const listNameByInputId = rawValue.listNameByInputId || {};
            rawValue.alternateInputIds.forEach((altId: string) => {
              // Utiliser le listName spécifique à cet inputId s'il existe
              const altListName = listNameByInputId[altId] || fieldData.listName;
              const altFieldData = { ...fieldData, id: altId, listName: altListName };
              this.addFlatFieldToMap(flatFieldsMap, altId, altFieldData);
            });
          }
        }
        this.scanFormGroupToFlatFields(control, currentPath, flatFieldsMap);
      }
    });
  }

  /**
   * Parcourt un FormArray et remplit la map flatFields.
   */
  private scanFormArrayToFlatFields(array: FormArray, currentPath: string, flatFieldsMap: Map<string, FlatField[]>): void {
    array.controls.forEach((arrayControl, index) => {
      if (arrayControl instanceof FormGroup) {
        const arrayPath = `${currentPath}.${index}`;
        const rawValue = arrayControl.getRawValue();
        if (this.hasInputId(rawValue)) {
          const fieldData = this.createFlatFieldFromRawValue(arrayControl, arrayPath, rawValue);
          this.addFlatFieldToMap(flatFieldsMap, rawValue.inputId, fieldData);

          // Enregistrer aussi les alternateInputIds pour les champs qui partagent le même jsonPath
          if (rawValue.alternateInputIds && Array.isArray(rawValue.alternateInputIds)) {
            const listNameByInputId = rawValue.listNameByInputId || {};
            rawValue.alternateInputIds.forEach((altId: string) => {
              // Utiliser le listName spécifique à cet inputId s'il existe
              const altListName = listNameByInputId[altId] || fieldData.listName;
              const altFieldData = { ...fieldData, id: altId, listName: altListName };
              this.addFlatFieldToMap(flatFieldsMap, altId, altFieldData);
            });
          }
        }
        this.scanFormGroupToFlatFields(arrayControl, arrayPath, flatFieldsMap);
      }
    });
  }

  /**
   * Transforme un objet JSON en flatFields (pour les données backend).
   */
  scanJsonToFlatFields(jsonData: any): void {
    // Auto-initialiser les paths si nécessaire en détectant le type de formulaire depuis le JSON
    this.autoInitializePathsFromJson(jsonData);

    const newFlatFields = new Map<string, FlatField[]>();
    this.scanJsonObjectToFlatFields(jsonData, '', newFlatFields);
    this.flatFields.next(newFlatFields);
    this.eformFlatFieldsReady = true;
  }

  /**
   * Détecte automatiquement le type de formulaire depuis le JSON et initialise les paths si nécessaire.
   */
  private autoInitializePathsFromJson(jsonData: any): void {
    if (DuplicableElementPathEnum.isInitialized()) {
      return; // Déjà initialisé
    }

    // Essayer de détecter le type de formulaire depuis la structure du JSON
    const ann = jsonData?.jsonSaisieFormulaire?.ann || jsonData?.ann;
    if (!ann) {
      return;
    }

    // Les types de formulaire EForm possibles
    const eformTypes = [
      'priorInformationNotice',
      'contractNotice',
      'contractAwardNotice',
      'businessRegistrationInformationNotice'
    ];

    for (const formType of eformTypes) {
      if (ann[formType]) {
        DuplicableElementPathEnum.initializePaths(formType as any);
        return;
      }
    }
  }

  /**
   * Parcourt récursivement un objet JSON et remplit la map flatFields.
   */
  private scanJsonObjectToFlatFields(obj: any, parentPath: string, flatFieldsMap: Map<string, FlatField[]>, lotIndex?: number): void {
    if (!obj || typeof obj !== 'object') return;

    Object.entries(obj).forEach(([key, value]) => {
      const currentPath = parentPath ? `${parentPath}.${key.toLowerCase()}` : key.toLowerCase();

      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (item && typeof item === 'object') {
            const arrayPath = `${currentPath}.${index}`;

            // Déterminer l'index du lot si on est dans procurementprojectlot
            const currentLotIndex = key.toLowerCase() === 'procurementprojectlot' ? index : lotIndex;

            if (this.isJsonWithInputId(item)) {
              const fieldData = this.createFlatFieldFromJson(arrayPath, item, currentLotIndex);
              this.addFlatFieldToMap(flatFieldsMap, item.inputId, fieldData);
            }
            this.scanJsonObjectToFlatFields(item, arrayPath, flatFieldsMap, currentLotIndex);
          }
        });
      } else if (value && typeof value === 'object') {
        if (this.isJsonWithInputId(value)) {
          const fieldData = this.createFlatFieldFromJson(currentPath, value, lotIndex);
          this.addFlatFieldToMap(flatFieldsMap, (value as any).inputId, fieldData);
        }

        // 🔧 FIX 2026-01-22: Détecter BT-137-Lot par le nom de propriété 'id' dans procurementprojectlot
        // Le champ 'id' dans procurementprojectlot correspond à BT-137-Lot
        // 🔧 FIX 2026-01-22 v2: Filtrer pour ne garder que les valeurs LOT-XXXX ou PAR-XXXX (pas les index numériques)
        if (key.toLowerCase() === 'id' && parentPath.includes('procurementprojectlot') && (value as any).val !== undefined) {
          const valStr = String((value as any).val);
          // Ne prendre que les vraies valeurs d'identifiant de lot (LOT-0001, PAR-0001, etc.)
          if (valStr.startsWith('LOT-') || valStr.startsWith('PAR-')) {
            const fieldData = this.createFlatFieldFromJson(currentPath, { ...(value as any), inputId: 'BT-137-Lot' }, lotIndex);
            this.addFlatFieldToMap(flatFieldsMap, 'BT-137-Lot', fieldData);
          }
        }

        this.scanJsonObjectToFlatFields(value, currentPath, flatFieldsMap, lotIndex);
      }
    });
  }

  /**
   * 🔧 FIX 2026-04-21: Infère le type duplicable depuis l'inputId quand extractDuplicableInfo
   * ne trouve pas de type (le path FormGroup n'utilise pas les noms JSON comme procurementprojectlot).
   * Exemple: 'BT-539-Lot' → 'LOT', 'BT-3202-Contract' → 'CON', etc.
   */
  private getDuplicableTypeFromInputId(inputId: string): string | null {
    if (!inputId) return null;
    // Détection par le suffixe de section (ex: BT-539-Lot, BT-195(BT-540)-Lot)
    if (/-Lot/.test(inputId)) return 'LOT';
    if (/-Part/.test(inputId)) return 'PAR';
    if (/-Organization/.test(inputId) || /-Company/.test(inputId)) return 'ORG';
    if (/-Contract/.test(inputId)) return 'CON';
    if (/-Tenderer/.test(inputId)) return 'TPA';
    if (/-Tender/.test(inputId)) return 'TEN';
    if (/-Result/.test(inputId)) return 'RES';
    return null;
  }

  /**
   * Crée un FlatField à partir d'un FormGroup et de sa valeur brute.
   */
  createFlatFieldFromRawValue(control: FormGroup, path: string, rawValue: any): FlatField {
    let duplicableInfo = this.extractDuplicableInfo(path, rawValue.inputId);

    // 🔧 FIX 2026-05-12: Extraire le code d'unité depuis rawValue (identique à createFlatFieldFromJson)
    // Sans ce correctif, unitCodeValue restait toujours null → la normalisation du champ mesure
    // échouait (ex: BT-36-Lot = 6 ans) et la comparaison se faisait sur la valeur brute (6 <= 1460)
    // au lieu de la valeur en jours (2190 <= 1460), ce qui rendait la condition LESS_THAN_OR_EQUAL
    // incorrectement vraie → BT-109-Lot masqué à tort.
    let unitCodeValue: string | null = null;
    if (rawValue && typeof rawValue === 'object') {
      const uc = rawValue.unitcode ?? rawValue.unitCode ?? rawValue.unit ?? null;
      if (uc != null) {
        unitCodeValue = (typeof uc === 'object' && uc !== null && uc.val) ? String(uc.val) : String(uc);
      }
    }

    // 🔧 FIX 2026-04-21: Si extractDuplicableInfo ne trouve pas de type (le path FormGroup
    // n'utilise pas les noms JSON), inférer le type depuis l'inputId.
    // Exemple: BT-539-Lot dans un FormGroup GR-Lot → path ne contient pas 'procurementprojectlot'
    // donc extractDuplicableInfo retourne {type: null, index: null}.
    // On infère 'LOT' depuis l'ID et on laisse le fallback (actuallyIndexLotOrPar) fixer l'index.
    if (!duplicableInfo.type && rawValue.inputId) {
      const inferredType = this.getDuplicableTypeFromInputId(rawValue.inputId);
      if (inferredType) {
        duplicableInfo = { type: inferredType, index: 0 };
      }
    }

    if (!path.startsWith('jsonsaisieformulaire.ann.')) {
      path = `jsonsaisieformulaire.ann.${path}`;
    }

    // Utiliser l'index réel affiché si disponible, sinon fallback sur l'index extrait du path
    let effectiveIndex = duplicableInfo.index;
    if (duplicableInfo.type) {
      // 🔧 FIX 2026-04-21: Pour les types LOT/PAR, ne pas utiliser displayedRealIndexByType
      // car il est mis à jour UNIQUEMENT lors de la création d'un nouveau lot (newStep()),
      // mais PAS lors de la navigation vers un lot existant. Cela provoque un index périmé
      // (ex: LOT=1 après création du Lot 2, toujours 1 quand on revient sur le Lot 1).
      // Pour LOT/PAR, on utilise toujours le fallback basé sur actuallyIndexLotOrPar,
      // qui est mis à jour correctement dans getDuplicableEntity() à chaque navigation.
      const isLotOrPar = duplicableInfo.type === 'LOT' || duplicableInfo.type === 'PAR';
      const realIndex = isLotOrPar ? undefined : this.getDisplayedRealIndex(duplicableInfo.type);

      if (realIndex !== undefined) {
        effectiveIndex = realIndex;
        path = this.updatePathWithRealIndex(path, duplicableInfo.index ?? 0, realIndex);
      } else {
        const fallbackIndex = this.getDisplayedElementIndexFallback(duplicableInfo.type);
        if (fallbackIndex >= 0) {
          effectiveIndex = fallbackIndex;
          path = this.updatePathWithRealIndex(path, duplicableInfo.index ?? 0, fallbackIndex);
        }
      }
    }

    return {
      path,
      value: rawValue.val,
      id: rawValue.inputId,
      type: control.constructor.name,
      disabled: control.disabled || false,
      isDuplicableField: !!duplicableInfo.type,
      duplicableType: duplicableInfo.type || undefined,
      duplicableIndex: effectiveIndex ?? undefined,
      isDisplayed: duplicableInfo.type ? this.isDuplicableFieldDisplayed(duplicableInfo.type, effectiveIndex!) : true,
      unitCodeValue,
      listName: rawValue.listName || undefined
    };
  }

  /**
   * Met à jour un path pour remplacer l'index local par l'index réel.
   * @param path Le path original
   * @param localIndex L'index local (généralement 0)
   * @param realIndex L'index réel de l'élément duplicable
   */
  private updatePathWithRealIndex(path: string, localIndex: number | null, realIndex: number): string {
    if (localIndex === null) return path;

    // Remplacer l'index dans le path (ex: organization.0 -> organization.2)
    const patterns = [
      /(\borganization\.)(\d+)/i,
      /(\bsettledcontract\.)(\d+)/i,
      /(\btender\.)(\d+)/i,
      /(\blottender\.)(\d+)/i,
      /(\btenderingparty\.)(\d+)/i,
      /(\bprocurementprojectlot\.)(\d+)/i,
      /(\blotresult\.)(\d+)/i
    ];

    let updatedPath = path;
    for (const pattern of patterns) {
      const match = path.match(pattern);
      if (match && parseInt(match[2], 10) === localIndex) {
        updatedPath = path.replace(pattern, `$1${realIndex}`);
        break;
      }
    }

    return updatedPath;
  }

  /**
   * Crée un FlatField à partir d'un objet JSON (backend).
   */
  private createFlatFieldFromJson(path: string, json: any, lotIndex?: number): FlatField {
    const duplicableInfo = this.extractDuplicableInfo(path, json.inputId);
    let unitCodeValue: string | null = null;
    if (json && typeof json === 'object') {
      unitCodeValue = (json.unitcode ?? json.unitCode ?? json.unit ?? null) as any;
      if (!unitCodeValue) {
        unitCodeValue =
          (json.unitcode && typeof json.unitcode === 'object' && json.unitcode.val) ? json.unitcode.val :
            (json.unitCode && typeof json.unitCode === 'object' && json.unitCode.val) ? json.unitCode.val :
              null;
      }
      if (unitCodeValue != null) unitCodeValue = String(unitCodeValue);
    }
    // Normaliser le path pour qu'il commence par 'jsonsaisieformulaire.ann.'
    if (!path.startsWith('jsonsaisieformulaire.ann.')) {
      path = `jsonsaisieformulaire.ann.${path}`;
    }

    // Utiliser lotIndex si fourni, sinon utiliser l'index extrait du path
    const effectiveIndex = lotIndex !== undefined ? lotIndex : duplicableInfo.index;

    return {
      path,
      value: json.val,
      id: json.inputId,
      type: 'FormGroup',
      disabled: false,
      isDuplicableField: !!duplicableInfo.type || lotIndex !== undefined,
      duplicableType: duplicableInfo.type || (lotIndex !== undefined ? 'LOT' : undefined),
      duplicableIndex: effectiveIndex ?? undefined,
      isDisplayed: duplicableInfo.type ? this.isDuplicableFieldDisplayed(duplicableInfo.type, effectiveIndex!) : true,
      unitCodeValue,
      listName: json.listName || json.listname || undefined
    };
  }

  /**
   * Ajoute un FlatField à la map flatFields.
   * Compare par path + listName pour éviter d'écraser des champs logiquement différents.
   * Pour les champs duplicables de niveau supérieur (TEN, RES, ORG, etc.), on compare aussi par duplicableIndex.
   */
  addFlatFieldToMap(flatFieldsMap: Map<string, FlatField[]>, inputId: string, fieldData: FlatField): void {
    if (!flatFieldsMap.has(inputId)) {
      flatFieldsMap.set(inputId, []);
    }
    const existingFields = flatFieldsMap.get(inputId)!;

    // 🔧 FIX 2026-04-21: Pour les champs duplicables, comparer aussi par duplicableIndex
    // afin de ne pas écraser l'entrée d'un autre lot (même path FormGroup, index différent).
    // Exemple : BT-539-Lot sur Lot 1 (duplicableIndex=0) et Lot 2 (duplicableIndex=1) ont
    // le même path FormGroup → sans ce guard, Lot 2 écraserait Lot 1.
    const existingIndex = existingFields.findIndex(field => {
      const pathMatch = field.path === fieldData.path && field.listName === fieldData.listName;
      if (!pathMatch) return false;
      // Si les deux champs sont duplicables, vérifier aussi l'index
      if (field.isDuplicableField && fieldData.isDuplicableField) {
        return field.duplicableIndex === fieldData.duplicableIndex;
      }
      return true;
    });

    if (existingIndex !== -1) {
      // Mettre à jour le champ existant
      existingFields[existingIndex] = fieldData;
    } else {
      existingFields.push(fieldData);
    }
  }

  /**
   * Vérifie si un objet JSON possède un inputId.
   */
  private isJsonWithInputId(value: any): value is { inputId: string; val: any } {
    return value && typeof value === 'object' && 'inputId' in value && !!value.inputId;
  }

  /**
   * Vérifie si un objet contient un inputId valide.
   */
  hasInputId(rawValue: any): boolean {
    return Object.keys(rawValue).includes('inputId') && rawValue.inputId;
  }

  /**
   * Met à jour la map flatFields avec les nouvelles données du champ.
   */
  updateFlatFields(inputId: string, fieldData: FlatField): void {
    const currentFields = this.flatFields.value;
    const existingFields = currentFields.get(inputId) || [];
    const existingFieldIndex = existingFields.findIndex(field => field.path === fieldData.path);

    if (existingFieldIndex !== -1) {
      existingFields[existingFieldIndex] = fieldData;
    } else {
      existingFields.push(fieldData);
    }

    currentFields.set(inputId, existingFields);
    this.flatFields.next(currentFields);
  }

  /**
   * Met à jour directement la valeur d'un champ dans flatFields par son inputId et contexte duplicable.
   * Cette méthode doit être appelée à chaque modification de valeur pour garder flatFields synchronisé.
   *
   * @param inputId L'identifiant du champ (ex: OPT-321-Tender)
   * @param value La nouvelle valeur
   * @param duplicableType Le type d'élément duplicable (TEN, RES, ORG, etc.) ou undefined si non duplicable
   * @param duplicableIndex L'index de l'élément duplicable ou undefined si non duplicable
   */
  updateFlatFieldValue(inputId: string, value: any, duplicableType?: string, duplicableIndex?: number): void {
    const currentFields = this.flatFields.value;
    let existingFields = currentFields.get(inputId);

    // Si le champ n'existe pas encore dans flatFields pour cet inputId, le créer
    if (!existingFields || existingFields.length === 0) {
      if (duplicableType !== undefined && duplicableIndex !== undefined) {
        // Créer un nouveau FlatField pour cet élément duplicable
        const newField: FlatField = {
          path: `jsonsaisieformulaire.ann.${duplicableType.toLowerCase()}.${duplicableIndex}`,
          value: value,
          id: inputId,
          type: 'FormGroup',
          disabled: false,
          isDuplicableField: true,
          duplicableType: duplicableType,
          duplicableIndex: duplicableIndex,
          isDisplayed: true,
          unitCodeValue: null,
          listName: undefined
        };
        currentFields.set(inputId, [newField]);
        this.flatFields.next(currentFields);
      }
      return;
    }

    let fieldUpdated = false;
    let fieldFoundForIndex = false;

    existingFields.forEach(field => {
      // Si c'est un champ duplicable, vérifier le type et l'index
      if (duplicableType !== undefined && duplicableIndex !== undefined) {
        if (field.isDuplicableField &&
            field.duplicableType === duplicableType &&
            field.duplicableIndex === duplicableIndex) {
          field.value = value;
          fieldUpdated = true;
          fieldFoundForIndex = true;
        }
      } else {
        // Pour les champs non duplicables, mettre à jour tous les champs avec cet inputId
        if (!field.isDuplicableField) {
          field.value = value;
          fieldUpdated = true;
        }
      }
    });

    // Si aucun champ n'a été trouvé pour cet index duplicable, en créer un nouveau
    if (duplicableType !== undefined && duplicableIndex !== undefined && !fieldFoundForIndex) {
      // Utiliser un champ existant comme modèle pour le path
      const templateField = existingFields[0];
      const newField: FlatField = {
        ...templateField,
        value: value,
        duplicableIndex: duplicableIndex,
        isDisplayed: true
      };
      // Mettre à jour le path avec le bon index
      if (templateField.path) {
        newField.path = this.updatePathWithRealIndex(templateField.path, templateField.duplicableIndex ?? 0, duplicableIndex);
      }
      existingFields.push(newField);
      fieldUpdated = true;
    }

    if (fieldUpdated) {
      this.flatFields.next(currentFields);
    }
  }

  /**
   * Extrait le type et l'index d'un objet duplicable à partir de son path et de son id.
   */
  extractDuplicableInfo(path: string, id: string): { type: string | null, index: number | null } {
    // Vérifier si les paths sont initialisés avant de les utiliser
    if (!DuplicableElementPathEnum.isInitialized()) {
      return {type: null, index: null};
    }
    const paths = DuplicableElementPathEnum.paths;
    if (!paths) {
      return {type: null, index: null};
    }

    // Logique spéciale pour les champs Tenderer
    if (id.includes('Tenderer')) {
      const indexPatterns = [
        /tenderingparty\.(\d+)/i,
        /\.(\d+)\./,
        /\.(\d+)$/,
        /_(\d+)_/,
        /_(\d+)$/
      ];

      for (const pattern of indexPatterns) {
        const match = path.match(pattern);
        if (match) {
          return {
            type: 'TPA' as string,
            index: parseInt(match[1], 10)
          };
        }
      }
      return {type: 'TPA', index: 0};
    }

    // Logique spéciale pour les champs Tender (offres) - gérer les formats tender[] et lottender
    if (id.includes('Tender') && !id.includes('Tenderer')) {
      const tenderPatterns = [
        /lottender\[\](\d+)/i,
        /lottender\.(\d+)/i,
        /tender\[\](\d+)/i,
        /tender\.(\d+)/i
      ];

      for (const pattern of tenderPatterns) {
        const match = path.toLowerCase().match(pattern);
        if (match) {
          return {
            type: 'TEN',
            index: parseInt(match[1], 10)
          };
        }
      }
    }

    // Logique spéciale pour les champs LotResult (résultats des lots)
    if (id.includes('LotResult')) {
      const lotResultPatterns = [
        /lotresult\[\](\d+)/i,
        /lotresult\.(\d+)/i
      ];

      for (const pattern of lotResultPatterns) {
        const match = path.toLowerCase().match(pattern);
        if (match) {
          return {
            type: 'RES',
            index: parseInt(match[1], 10)
          };
        }
      }
    }

    const lowercasePath = path.toLowerCase();

    for (const [key, basePath] of Object.entries(paths)) {
      const basePathLower = basePath.toLowerCase();
      const basePathParts = basePathLower.split('.');
      const lastPart = basePathParts[basePathParts.length - 1];
      const pattern = new RegExp(`${lastPart}\\.(\\d+)`);
      const match = lowercasePath.match(pattern);

      if (match) {
        if (lastPart === 'procurementprojectlot') {
          const type = id.includes('Part') ? 'PAR' : 'LOT';
          return {type, index: parseInt(match[1], 10)};
        }
        return {type: key, index: parseInt(match[1], 10)};
      }
    }
    return {type: null, index: null};
  }

  /**
   * Vérifie si un champ duplicable est affiché selon l'index courant.
   */
  isDuplicableFieldDisplayed(type: string, index: number): boolean {
    const currentIndex = this.indexTrackerService.getLastIndex(type) - 1;
    return currentIndex === index;
  }

  /**
   * Modifie un path pour remplacer l'index après le chemin de l'enum par l'index fourni
   */
  updatePathIndex(path: string, enumPath: string, newIndex: number): string {
    const basePathIndex = path.indexOf(enumPath);
    if (basePathIndex === -1) return path;

    const beforeBasePath = path.substring(0, basePathIndex);
    const afterBasePath = path.substring(basePathIndex + enumPath.length);

    const parts = afterBasePath.split('.');
    parts[1] = newIndex.toString();

    return beforeBasePath + enumPath + parts.join('.');
  }

  /**
   * 🔧 FIX 2026-01-22: Scanne les données du sessionStorage pour les éléments duplicables
   * et les ajoute dans flatFields. Cela permet d'avoir les valeurs de tous les lots
   * disponibles pour l'évaluation des conditions, même avant que les lots soient affichés.
   *
   * @param sessionStorageKeys Les clés du sessionStorage à scanner (ex: ['GR-Lot'])
   * @param basePath Le chemin de base pour construire le path (ex: 'jsonsaisieformulaire.ann.contractawardnotice')
   */
  scanSessionStorageToFlatFields(sessionStorageKeys: string[], basePath: string): void {
    const currentMap = new Map(this.flatFields.value);

    sessionStorageKeys.forEach(key => {
      const storedData = sessionStorage.getItem(key);
      if (!storedData) return;

      try {
        const items = JSON.parse(storedData);
        if (!Array.isArray(items)) return;


        items.forEach((item, index) => {
          this.scanObjectToFlatFields(item, `${basePath}.${index}`, currentMap, index);
        });
      } catch (e) {
        console.error(`[scanSessionStorageToFlatFields] Erreur parsing ${key}:`, e);
      }
    });

    this.flatFields.next(currentMap);
  }

  /**
   * Scanne récursivement un objet JSON et ajoute les champs dans flatFields
   */
  private scanObjectToFlatFields(obj: any, path: string, map: Map<string, FlatField[]>, duplicableIndex: number, fieldName?: string): void {
    if (!obj || typeof obj !== 'object') return;

    // Si l'objet a un inputId, c'est un champ avec inputId explicite
    if (obj.inputId) {
      const fieldData: FlatField = {
        path: path + '.val',
        value: obj.val,
        id: obj.inputId,
        type: 'FromSessionStorage',
        disabled: false,
        isDuplicableField: true,
        duplicableType: 'LOT',
        duplicableIndex: duplicableIndex,
        isDisplayed: false, // Non affiché car venant du sessionStorage
        unitCodeValue: obj.unitcode || obj.unitCode || null,
        listName: obj.listName || obj.listname || undefined
      };

      this.addFlatFieldToMap(map, obj.inputId, fieldData);
    }
    // Si l'objet a une propriété 'val' et qu'on connaît le nom du champ, on peut déduire l'inputId
    else if (obj.hasOwnProperty('val') && fieldName) {
      // Les champs comme 'id' dans ProcurementProjectLot correspondent à BT-137-Lot
      const inputIdMapping: { [key: string]: string } = {
        'id': 'BT-137-Lot',
        'procurementtypecode': 'BT-23-Lot',
        // Ajouter d'autres mappings si nécessaire
      };

      const inputId = inputIdMapping[fieldName.toLowerCase()];
      if (inputId) {
        const fieldData: FlatField = {
          path: path + '.val',
          value: obj.val,
          id: inputId,
          type: 'FromSessionStorage',
          disabled: false,
          isDuplicableField: true,
          duplicableType: 'LOT',
          duplicableIndex: duplicableIndex,
          isDisplayed: false,
          unitCodeValue: obj.unitcode || obj.unitCode || null,
          listName: obj.listName || obj.listname || undefined
        };

        this.addFlatFieldToMap(map, inputId, fieldData);
      }
    }

    // Parcourir les propriétés de l'objet
    Object.keys(obj).forEach(key => {
      if (key === 'inputId' || key === 'val' || key === 'listName' || key === 'listname') return;

      const value = obj[key];
      const newPath = `${path}.${key}`;

      if (Array.isArray(value)) {
        value.forEach((item, i) => {
          this.scanObjectToFlatFields(item, `${newPath}.${i}`, map, duplicableIndex, key);
        });
      } else if (value && typeof value === 'object') {
        this.scanObjectToFlatFields(value, newPath, map, duplicableIndex, key);
      }
    });
  }
}
